import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../utils/api";
import { useMyTimer } from "../context/TimerContext";
import "./TimerSwitchModal.css";
import { timeCategoryOptions } from "../utils/timeCategories";

const CATEGORIES = timeCategoryOptions(["labor", "drive", "break", "lunch", "training", "comeback", "admin"]);

const TimerSwitchModal = ({ currentTimer, onClose, presetOrderId, presetServiceId }) => {
  const { switchActivity, clockIn, pending, activeTimer } = useMyTimer();
  const [category, setCategory] = useState(currentTimer?.category || "labor");
  const [orderId, setOrderId] = useState(presetOrderId || currentTimer?.order_id || "");
  const [serviceId, setServiceId] = useState(presetServiceId || currentTimer?.service_id || "");
  const [note, setNote] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderResults, setOrderResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);

  // Search orders as user types
  useEffect(() => {
    if (!orderSearch || orderSearch.length < 2) { setOrderResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(`/orders?search=${encodeURIComponent(orderSearch)}&limit=10`);
        if (res.ok) {
          const data = await res.json();
          setOrderResults(data.data || data || []);
        }
      } catch (err) { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [orderSearch]);

  // Load services when an order is selected
  useEffect(() => {
    if (!orderId) { setServices([]); return; }
    (async () => {
      try {
        // The order page's own details call (there is no /admin/orders/:id/details).
        const res = await fetchWithAuth(`/orders/${orderId}/details`);
        if (res.ok) {
          const data = await res.json();
          const list = data.services || [];
          setServices(list);
          // A service from another order (the timer's last one) can't come along.
          setServiceId((cur) => (cur && !list.some((s) => String(s.id) === String(cur)) ? "" : cur));
        }
      } catch (err) { /* ignore */ }
    })();
  }, [orderId]);

  const handleSubmit = async () => {
    setError(null);
    try {
      const payload = {
        order_id: orderId || null,
        service_id: serviceId || null,
        category,
        note: note || null,
      };
      // If there's an active timer, switch; otherwise clock in
      if (activeTimer) {
        await switchActivity(payload);
      } else {
        await clockIn(payload);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  const orderRequired = category === "labor" || category === "comeback";

  return (
    <div className="timer-switch-overlay" onClick={onClose}>
      <div className="timer-switch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="timer-switch-header">
          <h3>{activeTimer ? "Switch Activity" : "Clock In"}</h3>
          <button className="timer-switch-close" onClick={onClose}>&times;</button>
        </div>

        <div className="timer-switch-body">
          {/* Category selector */}
          <label className="timer-switch-label">Category</label>
          <div className="timer-switch-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`timer-switch-cat ${category === c.value ? "selected" : ""}`}
                style={{ borderColor: category === c.value ? c.color : "var(--border)", color: category === c.value ? c.color : "var(--text-secondary)" }}
                onClick={() => setCategory(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Order + service */}
          {(category === "labor" || category === "comeback" || category === "drive") && (
            <>
              <label className="timer-switch-label">
                Order {orderRequired && <span className="timer-switch-required">*</span>}
              </label>
              {selectedOrder ? (
                <div className="timer-switch-selected-order">
                  <span>
                    <strong>#{selectedOrder.order_number}</strong>
                    {selectedOrder.title && <> — {selectedOrder.title}</>}
                  </span>
                  <button onClick={() => { setSelectedOrder(null); setOrderId(""); setOrderSearch(""); setServiceId(""); }}>&times;</button>
                </div>
              ) : (
                <>
                  <input
                    className="timer-switch-input"
                    placeholder="Search orders..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                  />
                  {orderResults.length > 0 && (
                    <div className="timer-switch-order-results">
                      {orderResults.map((o) => (
                        <button
                          key={o.id}
                          className="timer-switch-order-result"
                          onClick={() => {
                            if (String(o.id) !== String(orderId)) setServiceId("");
                            setSelectedOrder(o);
                            setOrderId(o.id);
                            setOrderSearch("");
                            setOrderResults([]);
                          }}
                        >
                          <strong>#{o.order_number}</strong>
                          {o.title && <span> — {o.title}</span>}
                          {o.customer_first_name && <span className="timer-switch-order-meta">{o.customer_first_name} {o.customer_last_name}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {services.length > 0 && (
                <>
                  <label className="timer-switch-label">Service (optional)</label>
                  <select className="timer-switch-input" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                    <option value="">— None —</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>{s.name || "Untitled Service"}</option>
                    ))}
                  </select>
                </>
              )}
            </>
          )}

          <label className="timer-switch-label">Note (optional)</label>
          <textarea
            className="timer-switch-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What are you working on?"
            rows={2}
          />

          {error && <div className="timer-switch-error">{error}</div>}
        </div>

        <div className="timer-switch-footer">
          <button className="timer-switch-btn-cancel" onClick={onClose} disabled={pending}>Cancel</button>
          <button
            className="timer-switch-btn-submit"
            onClick={handleSubmit}
            disabled={pending || (orderRequired && !orderId)}
          >
            {pending ? "..." : activeTimer ? "Switch" : "Clock In"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimerSwitchModal;
